// ============================================================
//  Page /parcours — liste des parcours
// ============================================================

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as api from "../lib/api";
import {
  DEFAULT_PARCOURS_CONFIG,
  NIVEAUX,
  type Parcours,
  type ParcoursInput,
} from "../lib/types";

const STALINGRAD_ID = "11111111-1111-1111-1111-111111111111";

export function ParcoursList() {
  const [items, setItems] = useState<Parcours[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      const list = await api.listParcours();
      setItems(list);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { reload(); }, []);

  async function onCreate(input: ParcoursInput) {
    await api.createParcours(input);
    setShowForm(false);
    await reload();
  }

  async function onDelete(p: Parcours) {
    if (!confirm(`Supprimer le parcours « ${p.nom} » ? Les étapes associées seront perdues.`)) return;
    try {
      await api.deleteParcours(p.id);
      await reload();
    } catch (e) {
      if (e instanceof api.ApiCallError && e.code === "parcours_in_use") {
        const ok = confirm(
          `${e.message}\n\nSupprimer aussi les groupes (et leurs progressions) liés à ce parcours ?`
        );
        if (!ok) return;
        try {
          await api.deleteParcours(p.id, { force: true });
          await reload();
        } catch (e2) {
          alert("Suppression forcée échouée : " + (e2 as Error).message);
        }
        return;
      }
      alert("Suppression échouée : " + (e as Error).message);
    }
  }

  return (
    <div className="questions-page">
      <header className="questions-head">
        <div>
          <h1>Parcours</h1>
          <p className="questions-sub">
            {items.length} parcours · une session active sélectionne un parcours par groupe
          </p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary">+ Nouveau parcours</button>
      </header>

      {error && <div className="page-error">Erreur : {error}</div>}
      {loading ? (
        <div className="page-loading">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="questions-empty">
          <p>Aucun parcours encore. Crée-en un pour pouvoir lancer une session.</p>
          <button onClick={() => setShowForm(true)} className="btn-primary">+ Créer le premier</button>
        </div>
      ) : (
        <div className="parcours-grid">
          {items.map((p) => (
            <div key={p.id} className="parcours-card">
              <div className="parcours-head">
                <h3>{p.nom}</h3>
                {p.niveau && <span className="badge badge-niveau">{p.niveau}</span>}
              </div>
              <div className="parcours-config">
                {p.config.blocus_actif && (
                  <span className="cfg-chip">🎲 {p.config.blocus_nombre} blocus aléa</span>
                )}
                {p.config.max_erreurs > 0 && (
                  <span className="cfg-chip">⚠️ Blocus à {p.config.max_erreurs} erreurs</span>
                )}
              </div>
              <div className="parcours-actions">
                <Link to={`/parcours/${p.id}`} className="btn-secondary">Éditer</Link>
                <button onClick={() => onDelete(p)} className="btn-danger-small">🗑</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <ParcoursForm
          onSubmit={onCreate}
          onCancel={() => setShowForm(false)}
        />
      )}
    </div>
  );
}

// ---------- Form modal ----------

function ParcoursForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (input: ParcoursInput) => Promise<void>;
  onCancel: () => void;
}) {
  const [nom, setNom] = useState("");
  const [niveau, setNiveau] = useState("");
  const [config, setConfig] = useState(DEFAULT_PARCOURS_CONFIG);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handle(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit({
        lieu_id: STALINGRAD_ID,
        nom: nom.trim(),
        niveau: niveau || null,
        config,
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal-form" onClick={(e) => e.stopPropagation()}>
        <h2>Nouveau parcours</h2>
        <form onSubmit={handle}>
          <label>
            <span>Nom *</span>
            <input
              type="text"
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              required
              autoFocus
              placeholder="Ex : Parcours CM1 découverte"
              maxLength={200}
            />
          </label>
          <div className="form-row">
            <label>
              <span>Niveau cible</span>
              <select value={niveau} onChange={(e) => setNiveau(e.target.value)}>
                <option value="">—</option>
                {NIVEAUX.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
          </div>

          <fieldset className="config-fieldset">
            <legend>Configuration BLOCUS</legend>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={config.blocus_actif}
                onChange={(e) => setConfig({ ...config, blocus_actif: e.target.checked })}
              />
              <span>Blocus aléatoire activé</span>
            </label>
            <div className="form-row">
              <label>
                <span>Nombre balises blocus aléa</span>
                <input
                  type="number"
                  min={0}
                  max={20}
                  value={config.blocus_nombre}
                  disabled={!config.blocus_actif}
                  onChange={(e) => setConfig({ ...config, blocus_nombre: parseInt(e.target.value || "0", 10) })}
                />
              </label>
              <label>
                <span>Max erreurs avant blocus</span>
                <input
                  type="number"
                  min={0}
                  max={10}
                  value={config.max_erreurs}
                  onChange={(e) => setConfig({ ...config, max_erreurs: parseInt(e.target.value || "0", 10) })}
                />
              </label>
            </div>
            <p className="config-hint">
              0 erreur max = pas de blocus sur erreurs. Le mot secret est défini sur la session, pas le parcours.
            </p>
          </fieldset>

          {error && <p className="form-error">✗ {error}</p>}
          <div className="modal-actions">
            <button type="button" onClick={onCancel} className="btn-secondary">Annuler</button>
            <button type="submit" disabled={submitting} className="btn-primary">
              {submitting ? "Création…" : "Créer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
